<template>
  <div>
  <validation-observer ref="form" #default="{ handleSubmit }">
    <v-form @submit.prevent="handleSubmit(onSaveAndAddNew)">
      <v-container fluid>
        <v-row
          justify="center"
          :no-gutters="$vuetify.breakpoint.smAndDown"
        >
          <v-col cols="12" xl="9" class="text-justify">
            <v-card flat class="transparent">
              <v-card-text class="pt-0">
                <v-row>
                  <v-col cols="12" sm="4">
                    <validation-provider
                      name="partname"
                      rules="required"
                      #default="{ errors }"
                    >
                      <v-autocomplete
                        clearable
                        filled
                        :label="$t('planning.part')"
                        :items="partList"
                        return-object
                        :error-messages="errors"
                        :disabled="saving"
                        item-text="partname"
                        v-model="selectedPart"
                        :title="selectedPart ? selectedPart.partname : ''"
                        @change="() => { onPartSelection(); checkHighestEfficiencyMachine(); }"
                      >
                        <template #item="{ item }">
                          <v-list-item-content>
                            <v-list-item-title v-text="item.partname"></v-list-item-title>
                            <v-list-item-subtitle v-text="item.partnumber"></v-list-item-subtitle>
                          </v-list-item-content>
                        </template>
                         <template #append-outer v-if="suggestedMachines.length > 0">
                          <v-tooltip bottom>
                            <template #activator="{ on, attrs }">
                              <v-btn
                                icon
                                small
                                color="success"
                                v-bind="attrs"
                                v-on="on"
                                @click="showSuggestedMachineDialog"
                                class="ml-1"
                              >
                                <v-icon>mdi-lightbulb-on</v-icon>
                              </v-btn>
                            </template>
                            <span>View Suggested Machines (Top 3)</span>
                          </v-tooltip>
                        </template>
                      </v-autocomplete>
                    </validation-provider>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <validation-provider
                      name="machinename"
                      rules="required"
                      #default="{ errors }"
                    >
                      <v-autocomplete
                        clearable
                        filled
                        :label="$t('planning.machine')"
                        :items="machineList"
                        :error-messages="errors"
                        :disabled="saving || !selectedPart"
                        :loading="fetchingMatrix"
                        item-text="machinename"
                        item-value="machinename"
                        v-model="selectedMachine"
                        :title="selectedMachine || ''"
                        @change="setPlanParameters"
                      ></v-autocomplete>
                    </validation-provider>
                  </v-col>
                  <v-col cols="12" sm="4" v-if="!isCnc">
                    <validation-provider
                      name="equipmentname"
                      rules="required"
                      #default="{ errors }"
                    >
                      <v-autocomplete
                        clearable
                        filled
                        :label="$t('planning.equipment')"
                        :items="equipmentList"
                        :error-messages="errors"
                        :disabled="saving || !selectedPart"
                        :loading="fetchingMatrix"
                        item-text="equipmentname"
                        item-value="equipmentname"
                        v-model="selectedEquipment"
                        :title="selectedEquipment || ''"
                        @change="setPlanParameters"
                      ></v-autocomplete>
                    </validation-provider>
                  </v-col>
                </v-row>
                <v-row v-if="showColorField || showCustomerField || isInjectionMolding">
                  <v-col cols="12" :sm="showColorField && showCustomerField ? 4 : 6"
                    v-if="showColorField">
                    <validation-provider
                      name="color"
                      #default="{ errors }"
                    >
                      <v-autocomplete
                        clearable
                        filled
                        :label="$t('planning.color')"
                        :items="colorList"
                        :error-messages="errors"
                        :disabled="saving"
                        item-text="colorname"
                        item-value="colorname"
                        v-model="selectedColor"
                        :title="selectedColor ? selectedColor.colorname : ''"
                      >
                        <template #item="{ item }">
                          <v-list-item-content>
                            <v-list-item-title v-text="item.colorname"></v-list-item-title>
                          </v-list-item-content>
                        </template>
                      </v-autocomplete>
                    </validation-provider>
                  </v-col>
                  <v-col cols="12" :sm="showColorField && showCustomerField ? 4 : 6"
                    v-if="showCustomerField">
                    <validation-provider
                      name="customer"
                      rules="required"
                      #default="{ errors }"
                    >
                      <v-combobox
                        clearable
                        filled
                        :label="$t('planning.customer')"
                        :items="customers"
                        :error-messages="errors"
                        :disabled="saving || !selectedPart"
                        item-text="customer"
                        item-value="customer"
                        v-model="selectedCustomer"
                        :title="selectedCustomer || ''"
                        @change="onCustomerSelection"
                      ></v-combobox>
                    </validation-provider>
                  </v-col>
                  <v-col cols="12" :sm="showColorField && showCustomerField ? 4 : 6"
                    v-if="isInjectionMolding">
                    <validation-provider
                      name="remark"
                      #default="{ errors }"
                    >
                      <v-text-field
                        :label="$t('planning.remark')"
                        type="text"
                        :disabled="saving"
                        :error-messages="errors"
                        outlined
                        v-model="plan.remark"
                        hide-details="auto"
                      ></v-text-field>
                    </validation-provider>
                  </v-col>
                </v-row>
                <div class="title mb-2">
                  {{ $t('planning.operationalParams') }}
                  <v-btn
                    small
                    outlined
                    class="text-none"
                    :disabled="!selectedMatrix || saving"
                    @click="editParams = !editParams"
                    color="primary"
                  >
                    <template v-if="!editParams">
                      <v-icon left small>mdi-pencil-outline</v-icon>
                      {{ $t('planning.editButton') }}
                    </template>
                    <template v-else>
                      <v-icon left small>mdi-check</v-icon>
                      {{ $t('planning.doneButton') }}
                    </template>
                  </v-btn>
                </div>
                <v-row>
                  <v-col cols="12" :sm="(isPress || isCnc) ? 3 : 4">
                    <validation-provider
                      name="cycletime"
                      rules="required|min_value:1"
                      #default="{ errors }"
                    >
                      <v-text-field
                        :label="$t('planning.cycletime')"
                        type="number"
                        :disabled="!editParams || saving"
                        :error-messages="errors"
                        prepend-inner-icon="mdi-clock-fast"
                        outlined
                        :suffix="$t('planning.secs')"
                        v-model="plan.stdcycletime"
                        hide-details="auto"
                      ></v-text-field>
                    </validation-provider>
                  </v-col>
                  <v-col cols="12" :sm="(isPress || isCnc) ? 3 : 4">
                    <validation-provider
                      name="delaytime"
                      rules="required|min_value:0"
                      #default="{ errors }"
                    >
                      <v-text-field
                        :label="$t('planning.delayTime')"
                        type="number"
                        :disabled="!editParams || saving"
                        :error-messages="errors"
                        prepend-inner-icon="mdi-cached"
                        outlined
                        :suffix="$t('planning.secs')"
                        v-model="plan.delaytime"
                        hide-details="auto"
                      ></v-text-field>
                    </validation-provider>
                  </v-col>
                  <v-col cols="12" :sm="(isPress || isCnc) ? 3 : 4">
                    <validation-provider
                      name="activecavity"
                      :rules="`required|max_value:${plan.cavity}|min_value:0.1`"
                      #default="{ errors }"
                    >
                      <v-text-field
                        :label="$t('planning.activeCavity')"
                        type="number"
                        :disabled="!editParams || saving"
                        :error-messages="errors"
                        prepend-inner-icon="mdi-tray-full"
                        outlined
                        v-model="plan.activecavity"
                        hide-details="auto"
                        @change="onCavityChange"
                      ></v-text-field>
                    </validation-provider>
                  </v-col>
                  <v-col cols="12" sm="3">
                    <validation-provider
                      name="strokes"
                      :rules="`${(isPress || isCnc)
                        ? 'required|numeric|min_value:1'
                        : ''}`"
                      #default="{ errors }"
                    >
                      <v-text-field
                        :label="$t('planning.strokes')"
                        type="number"
                        v-if="(isPress || isCnc)"
                        :disabled="!editParams || saving"
                        :error-messages="errors"
                        prepend-inner-icon="mdi-arrow-collapse-vertical"
                        outlined
                        v-model="plan.strokes"
                        hide-details="auto"
                      ></v-text-field>
                    </validation-provider>
                  </v-col>
                </v-row>
                <div class="caption" v-if="false">
                  <span class="error--text">
                    {{ $t('planning.paramsEdited') }}
                  </span>
                  <div>
                    {{ $t('planning.standardCycle') }}
                    <strong>{{ selectedMatrix.stdcycletime }}</strong> {{ $t('planning.secs') }}
                  </div>
                  <div>
                    {{ $t('planning.standardDelay') }}
                    <strong>{{ selectedMatrix.delaytime }}</strong> {{ $t('planning.secs') }}
                  </div>
                  <div>
                    {{ $t('planning.standardCavity') }}
                    <strong>{{ selectedMatrix.cavity }}</strong>
                  </div>
                  <div v-if="(isPress || isCnc)">
                    {{ $t('planning.standardStrokes') }}
                    <strong>{{ selectedMatrix.strokes }}</strong>
                  </div>
                </div>
                <div class="title mt-4 mb-2">
                  {{ $t('planning.planDetails') }}
                </div>
                <v-alert
                  v-if="showMissedProduction"
                  outlined
                  color="warning"
                  class="mb-4 bg-white"
                >
                  <v-row align="center" no-gutters>
                    <v-col class="grow">
                      <div class="d-flex align-center font-weight-bold mb-1">
                        <v-icon color="warning" small class="mr-2">mdi-exclamation-thick</v-icon>
                        {{ $t('planning.missedProductionTitle') }}
                      </div>
                      <div class="text-body-2 pl-6">
                        <span class="warning--text">{{ selectedMachine }}</span>
                        {{ $t('planning.missedProductionShots', { shots: missedShots }) }}

                        <span v-if="plan.activecavity">
                          {{ $t('planning.missedProductionPieces',
                           { pieces: missedPieces, cavity: plan.activecavity }) }}
                        </span>
                        <span v-else class="grey--text">
                          <em>{{ $t('planning.missedProductionNoEquipment') }}</em>
                        </span>
                      </div>
                    </v-col>
                    <v-col class="shrink">
                      <v-btn
                        outlined
                        color="warning"
                        small
                        class="text-none"
                        :disabled="!plan.activecavity"
                        @click="useNoPlanQuantity"
                      >
                        {{ $t('planning.useAsPlanQty') }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-alert>
                <v-row>
                  <v-col cols="12" sm="4">
                    <validation-provider
                      name="quantity"
                      rules="required|numeric|min_value:1"
                      #default="{ errors }"
                    >
                      <v-text-field
                        :label="$t('planning.quantity')"
                        type="number"
                        :disabled="saving"
                        :error-messages="errors"
                        prepend-inner-icon="mdi-package-variant-closed"
                        outlined
                        :suffix="$t('planning.pieces')"
                        v-model="plan.plannedquantity"
                        hide-details="auto"
                        @change="onQuantityChange"
                      ></v-text-field>
                    </validation-provider>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <validation-provider
                      name="scheduledstart"
                      rules="required|greater_than_now"
                      #default="{ errors }"
                    >
                      <v-text-field
                        :label="$t('planning.scheduledStartTime')"
                        type="datetime-local"
                        :error-messages="errors"
                        prepend-inner-icon="mdi-calendar-check-outline"
                        outlined
                        v-model="plan.scheduledstart"
                        hide-details="auto"
                        :disabled="saving"
                        @change="fetchEstimatedEnd"
                      >
                        <template #append-outer>
                          <v-tooltip bottom>
                            <template #activator="{ on, attrs }">
                              <v-icon
                                v-text="'$info'"
                                v-on="on"
                                v-bind="attrs"
                              ></v-icon>
                            </template>
                            <ul>
                              <li>
                                Scheduled start time is only used in calculating
                                planned vs actual performance.
                              </li>
                              <li>
                                Updating scheduled start time will not affect
                                the execution order of the plan.
                              </li>
                              <li>
                                Plans are picked automatically in FIFO fashion.
                              </li>
                              <li>
                                To update the execution order, please visit
                                <strong>re-order plans section</strong>.
                              </li>
                            </ul>
                          </v-tooltip>
                        </template>
                      </v-text-field>
                    </validation-provider>
                  </v-col>
                  <v-col cols="6" sm="2">
                    <v-checkbox
                      hide-details
                      :label="$t('planning.markAsStar')"
                      :disabled="saving"
                      v-model="plan.starred"
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="6" sm="2">
                    <v-checkbox
                      hide-details
                      :label="$t('planning.markAsTrial')"
                      :disabled="saving"
                      v-model="plan.trial"
                    ></v-checkbox>
                  </v-col>
                </v-row>
                <div>
                  {{ $t('planning.estimatedEnd') }}
                  <strong>{{ estimatedEndDisplay }}</strong>
                </div>
                <template v-if="familyParts.length">
                  <div class="title mt-4 mb-2">
                    {{ $t('planning.familyMold') }}
                  </div>
                    <v-data-table
                      :items="familyParts"
                      :headers="headers"
                      hide-default-footer
                      disable-pagination
                      item-key="_id"
                      show-select
                      dense
                      v-model="selectedFamilyParts"
                    >
                      <!-- eslint-disable-next-line -->
                      <template #item.activecavity="{ item }">
                        <v-responsive :max-width="100">
                          <validation-provider
                            name="familyactivecavity"
                            :vid="`cavity-${item._id}`"
                            :rules="`required|max_value:${item.cavity}|min_value:0.1`"
                            #default="{ errors }"
                          >
                            <v-text-field
                              v-model="item.activecavity"
                              type="number"
                              :label="$t('planning.activeCavity')"
                              :error-messages="errors"
                              single-line
                              dense
                              hide-details="auto"
                              @change="updateFamilyCavity({
                                id: item._id,
                                payload: item.activecavity,
                              })"
                            ></v-text-field>
                        </validation-provider>
                      </v-responsive>
                    </template>
                      <!-- eslint-disable-next-line -->
                      <template #item.plannedquantity="{ item }">
                        {{ item.plannedquantity }} {{ $t('planning.pieces') }}
                    </template>
                  </v-data-table>
                </template>
              </v-card-text>
              <v-card-actions>
                <v-btn
                  text
                  color="primary"
                  class="text-none"
                  :disabled="saving"
                  @click="exit"
                >
                  {{ $t('planning.exit') }}
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn
                  outlined
                  color="primary"
                  class="text-none"
                  :disabled="savingAndNew"
                  :loading="savingAndExit"
                  @click="onSaveAndExit"
                >
                  {{ $t('planning.saveExit') }}
                </v-btn>
                <v-btn
                  color="primary"
                  class="text-none"
                  :disabled="savingAndExit"
                  :loading="savingAndNew"
                  type="submit"
                >
                  {{ $t('planning.saveAddNew') }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-form>
  </validation-observer>

  <v-dialog v-model="showSuggestion" max-width="80%" persistent content-class="bottom-popup-dialog">
    <v-card class="pa-3 elevation-12">
      <v-card-title class="headline font-weight-bold primary--text text-center">
        🚀 Top 3 Suggested Machines
      </v-card-title>
      <v-card-text>
        <v-simple-table dense>
          <thead>
            <tr class="grey lighten-4">
              <th class="text-left">#</th>
              <th class="text-left">Machine Name</th>
              <th class="text-left">Part Name</th>
              <th class="text-left">Efficiency (%)</th>
              <th class="text-left">Active Cavity</th>
              <th class="text-left">Total Qty</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(machine, index) in suggestedMachines" :key="index">
              <td>{{ index + 1 }}</td>
              <td
                class="success--text font-weight-medium"
                style="cursor: pointer;"
                @click="selectSuggestedMachine(machine)"
              >
                {{ machine && machine.machinename }}
              </td>
              <td>{{ machine && machine.partname }}</td>
              <td class="success--text font-weight-bold">
                {{ machine && machine.efficiency_percent }}
              </td>
              <td>{{ machine && machine.activecavity }}</td>
              <td>{{ machine && machine.total_qty }}</td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card-text>
      <v-card-actions class="justify-center mt-2">
        <v-btn color="green darken-1" class="white--text" rounded @click="closeSuggestionDialog">
          OK, Got it!
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  </div>
</template>

<script>
import {
  mapState, mapActions, mapGetters, mapMutations,
} from 'vuex';
import { sortArray } from '@shopworx/services/util/sort.service';
import { formatDate } from '@shopworx/services/util/date.service';

export default {
  name: 'AddPlan',
  data() {
    return {
      headers: [
        { text: this.$t('planning.part'), value: 'partname' },
        { text: this.$t('planning.activeCavity'), value: 'activecavity' },
        { text: this.$t('planning.quantity'), value: 'plannedquantity' },
      ],
      editParams: false,
      savingAndExit: false,
      savingAndNew: false,
      fetchingMatrix: false,
      selectedPart: null,
      partMatrixList: [],
      familyParts: [],
      selectedFamilyParts: [],
      colorList: [],
      plan: null,
      selectedColor: null,
      selectedCustomer: null,
      selectedMachine: null,
      selectedEquipment: null,
      suggestedMachine: null,
      suggestedMachines: [],
      showSuggestion: false,
      missedShots: 0,
      customerOptions: [],
    };
  },
  computed: {
    ...mapState('productionPlanning', ['parts', 'partMatrix', 'machines', 'partWiseEffience', 'customerList']),
    ...mapState('webApp', ['appSchema']),
    ...mapState('user', ['me', 'licenses']),
    ...mapGetters('productionPlanning', ['selectedAsset', 'partMatrixTags']),
    ...mapGetters('helper', ['locale']),
    isInjectionMolding() {
      let result = false;
      if (this.selectedPart && this.selectedPart.assetid) {
        const assetName = this.selectedAsset(this.selectedPart.assetid);
        result = assetName === 'injectionmolding' || assetName === 'preform';
      }
      return result;
    },
    isPress() {
      let result = false;
      if (this.selectedPart && this.selectedPart.assetid) {
        result = this.selectedAsset(this.selectedPart.assetid) === 'press';
      }
      return result;
    },
    isCnc() {
      let result = false;
      if (this.selectedPart && this.selectedPart.assetid) {
        result = this.selectedAsset(this.selectedPart.assetid) === 'cnc';
      }
      return result;
    },
    areParamsEdited() {
      if (this.selectedMatrix) {
        const {
          stdcycletime,
          delaytime,
          cavity,
          strokes,
        } = this.selectedMatrix;
        return +this.plan.stdcycletime !== stdcycletime
          || +this.plan.delaytime !== delaytime
          || +this.plan.activecavity !== cavity
          || (this.isPress && +this.plan.strokes !== strokes)
          || (this.isCnc && +this.plan.strokes !== strokes);
      }
      return false;
    },
    selectedLicenseAssetId() {
      return this.licenses && this.licenses.length
        ? this.licenses[0].assetId
        : null;
    },
    selectedSchema() {
      if (!Array.isArray(this.appSchema) || !this.selectedLicenseAssetId) {
        return null;
      }
      return this.appSchema.find((element) => element.assetId === this.selectedLicenseAssetId)
      || null;
    },
    saving() {
      return this.savingAndExit || this.savingAndNew;
    },
    partList() {
      const sortedData = sortArray(this.parts, 'partname');
      return sortedData;
    },
    machineList() {
      let list = [...this.partMatrixList];
      if (this.selectedEquipment) {
        list = this.partMatrixList
          .filter((val) => (val.equipmentname === this.selectedEquipment));
        return sortArray(list, 'machinename');
      }
      return sortArray(list, 'machinename');
    },
    equipmentList() {
      let list = [...this.partMatrixList];
      if (this.selectedMachine) {
        list = this.partMatrixList
          .filter((val) => (val.machinename === this.selectedMachine));
        return sortArray(list, 'equipmentname');
      }
      return sortArray(list, 'equipmentname');
    },
    estimatedEndDisplay() {
      let res = '';
      if (this.plan.scheduledend) {
        res = formatDate(
          new Date(this.plan.scheduledend),
          'PPpp',
          { locale: this.locale },
        );
      }
      return res;
    },
    selectedMatrix() {
      if (!this.isCnc && this.selectedPart && this.selectedMachine && this.selectedEquipment) {
        const partMatrix = this.partMatrixList.filter((matrix) => (
          matrix.partname === this.selectedPart.partname
          && matrix.machinename === this.selectedMachine
          && (matrix.equipmentname === this.selectedEquipment)
        ));
        const customerMatrix = this.selectedCustomer
          ? partMatrix.filter((matrix) => matrix.customer === this.selectedCustomer)
          : partMatrix;
        const selectedMatrix = customerMatrix.length ? customerMatrix : partMatrix;
        return selectedMatrix.length
          ? selectedMatrix.sort((a, b) => (a.sequence || 0) - (b.sequence || 0))[0]
          : null;
      }
      if (this.isCnc && this.selectedPart && this.selectedMachine) {
        const partMatrix = this.partMatrixList.filter((matrix) => (
          matrix.partname === this.selectedPart.partname
          && matrix.machinename === this.selectedMachine
        ));
        const customerMatrix = this.selectedCustomer
          ? partMatrix.filter((matrix) => matrix.customer === this.selectedCustomer)
          : partMatrix;
        const selectedMatrix = customerMatrix.length ? customerMatrix : partMatrix;
        return selectedMatrix.length
          ? selectedMatrix.sort((a, b) => (a.sequence || 0) - (b.sequence || 0))[0]
          : null;
      }
      return null;
    },
    shots() {
      if (this.plan.plannedquantity && this.plan.activecavity) {
        return +this.plan.plannedquantity / +this.plan.activecavity;
      }
      return 0;
    },
    isShowColor() {
      let isColor = false;
      const found = this.selectedSchema;
      if (found && found.isShowColor) {
        isColor = true;
      }
      return isColor;
    },
    isShowCustomer() {
      let showCustomer = false;
      const found = this.selectedSchema;
      if (found) {
        const composition = (found.partMatrixComposition || [])
          .map((value) => String(value).toLowerCase());
        showCustomer = composition.includes('customer');
      }
      return showCustomer;
    },
    showColorField() {
      return this.isShowColor;
    },
    showCustomerField() {
      return this.isShowCustomer;
    },
    customers() {
      if (!this.isShowCustomer) {
        return [];
      }
      const normalizedCustomers = [
        ...(this.customerOptions || []),
        ...(this.customerList || []),
      ].map((record) => ({
        customer: record.customer || record.customername || record.name || record,
      })).filter((record) => record.customer);
      return [...new Map(normalizedCustomers.map((record) => [record.customer, record])).values()];
    },
    missedPieces() {
      if (this.missedShots > 0 && this.plan.activecavity) {
        return this.missedShots * +this.plan.activecavity;
      }
      return 0;
    },
    showMissedProduction() {
      return this.missedShots;
    },
  },
  created() {
    this.initPlan();
    this.fetchCustomerList();
  },

  methods: {
    ...mapMutations('helper', ['setAlert']),
    ...mapActions('productionPlanning', [
      'fetchPartMatrix',
      'isFamilyMold',
      'getFamilyParts',
      'getColors',
      'fetchCustomerList',
      'getScheduledEnd',
      'fetchLastPlan',
      'createPlans',
      'checkUnplannedProduction',
    ]),
    async selectSuggestedMachine(machine) {
      if (machine && machine.machinename) {
        this.showSuggestion = false;
        this.$set(this, 'selectedMachine', machine.machinename);
        await this.$nextTick();
        if (!this.isCnc && this.partMatrixList.length > 0) {
          const machineEquipment = this.partMatrixList.find(
            (matrix) => matrix.machinename === machine.machinename,
          );
          if (machineEquipment && machineEquipment.equipmentname) {
            this.$set(this, 'selectedEquipment', machineEquipment.equipmentname);
          }
        }
        await this.$nextTick();
        await this.setPlanParameters();
      }
    },
    closeSuggestionDialog() {
      this.showSuggestion = false;
    },
    showSuggestedMachineDialog() {
      if (this.suggestedMachines.length > 0) {
        this.showSuggestion = true;
      }
    },
    checkHighestEfficiencyMachine() {
      try {
        if (!this.selectedPart) {
          this.suggestedMachine = null;
          this.suggestedMachines = [];
          this.showSuggestion = false;
          return;
        }
        const partEffData = Array.isArray(this.partWiseEffience)
          ? this.partWiseEffience
          : JSON.parse(this.partWiseEffience || '[]');
        if (!partEffData || partEffData.length === 0) {
          this.suggestedMachine = null;
          this.suggestedMachines = [];
          this.showSuggestion = false;
          return;
        }
        const matchedParts = partEffData.filter(
          (item) => item.partname === this.selectedPart.partname,
        );
        if (matchedParts.length > 0) {
          const sortedMachines = matchedParts.sort(
            (a, b) => b.efficiency_percent - a.efficiency_percent,
          );
          this.suggestedMachines = sortedMachines.slice(0, 3);
          const [firstMachine] = this.suggestedMachines;
          this.suggestedMachine = firstMachine;
          this.showSuggestion = true;
        } else {
          this.suggestedMachine = null;
          this.suggestedMachines = [];
          this.showSuggestion = false;
        }
      } catch (error) {
        this.suggestedMachine = null;
        this.suggestedMachines = [];
        this.showSuggestion = false;
      }
    },
    async initPlan() {
      this.plan = {
        assetid: 0,
        partname: '',
        machinename: '',
        customer: '',
        stdcycletime: '',
        delaytime: '',
        cavity: '',
        activecavity: '',
        strokes: 1,
        plannedquantity: '',
        scheduledstart: formatDate(new Date(Math.ceil(new Date() / 9e5) * 9e5), 'yyyy-MM-dd\'T\'HH:mm'),
        scheduledend: '',
        starred: false,
        trial: false,
        sortindex: 0,
        status: 'notStarted',
        remark: '',
      };
      const colorData = await this.getColors();
      this.colorList = colorData;
    },
    clear() {
      this.initPlan();
      this.editParams = false;
      this.partMatrixList = [];
      this.familyParts = [];
      this.selectedFamilyParts = [];
      this.selectedColor = null;
      this.selectedCustomer = null;
      this.selectedMachine = null;
      this.selectedEquipment = null;
      this.suggestedMachine = null;
      this.suggestedMachines = [];
      this.showSuggestion = false;
      this.missedShots = 0;
      this.customerOptions = [];
      this.$nextTick(() => {
        this.$refs.form.reset();
      });
    },
    getUniqueRecords(records, key) {
      return [...new Map(
        records
          .filter((record) => record && record[key] !== undefined && record[key] !== null && record[key] !== '')
          .map((record) => [record[key], record]),
      ).values()];
    },
    refreshCustomerOptions() {
      if (!this.isShowCustomer) {
        this.customerOptions = [];
        return;
      }
      if (!this.selectedPart || !this.selectedMachine || (!this.isCnc && !this.selectedEquipment)) {
        this.customerOptions = [];
        return;
      }
      const partMatrix = this.partMatrixList.filter((matrix) => (
        matrix.partname === this.selectedPart.partname
        && matrix.machinename === this.selectedMachine
        && (this.isCnc || matrix.equipmentname === this.selectedEquipment)
      ));
      this.customerOptions = this.getUniqueRecords(partMatrix, 'customer').map((record) => ({
        customer: record.customer || record.customername || record.name || '',
      })).filter((record) => record.customer);
      if (this.customerOptions.length === 1 && !this.selectedCustomer) {
        [this.selectedCustomer] = this.customerOptions.map((record) => record.customer);
      }
    },
    async onPartSelection() {
      this.clear();
      if (this.selectedPart) {
        this.fetchingMatrix = true;
        this.partMatrixList = await this.fetchPartMatrix(this.selectedPart);
        if (!this.isShowColor) {
          this.selectedColor = this.selectedPart.color;
        }
        this.fetchingMatrix = false;
      }
    },
    onCustomerSelection() {
      if (this.selectedCustomer) {
        this.setPlanParameters();
      }
    },
    async setPlanParameters() {
      this.refreshCustomerOptions();
      if (this.selectedMachine) {
        this.missedShots = await this.checkUnplannedProduction(this.selectedMachine);
      } else {
        this.missedShots = 0;
      }

      if ((this.selectedMachine && this.isCnc)
          || (!this.isCnc && this.selectedMachine && this.selectedEquipment)) {
        this.familyParts = [];
        this.selectedFamilyParts = [];
        const {
          stdcycletime,
          delaytime,
          cavity,
          strokes,
          moldname,
          machinename,
          partname,
          customer,
        } = this.selectedMatrix;
        this.plan.cell = this.machines.find((e) => e.machinename === machinename).machinecell;
        this.plan.assetid = this.selectedPart.assetid;
        this.plan.partname = this.selectedPart.partname;
        this.plan.machinename = machinename;
        this.plan.customer = this.selectedCustomer || customer || '';
        this.plan.stdcycletime = +stdcycletime;
        this.plan.delaytime = +delaytime;
        this.plan.cavity = +cavity;
        this.plan.activecavity = +cavity;
        if (this.isPress) {
          this.plan.strokes = +strokes || 1;
        }
        if (this.isCnc) {
          this.plan.strokes = +strokes || 1;
        }
        if (this.isInjectionMolding) {
          const isFamily = await this.isFamilyMold(
            `?query=moldname=="${encodeURIComponent(moldname)}"`,
          );
          if (isFamily) {
            const familyParts = await this.getFamilyParts(
              `?query=moldname=="${encodeURIComponent(moldname)}"%26%26machinename=="${encodeURIComponent(machinename)}"%26%26partname!="${encodeURIComponent(partname)}"`,
            );
            this.familyParts = familyParts.map((p) => ({
              ...p,
              activecavity: p.cavity,
              plannedquantity: +p.cavity * this.shots,
            }));
            this.selectedFamilyParts = [...this.familyParts];
          }
        }
        // this.missedShots = await this.checkUnplannedProduction(this.plan.machinename);
      }
    },
    useNoPlanQuantity() {
      if (this.missedPieces > 0) {
        this.plan.plannedquantity = this.missedPieces;
        // We must manually call onQuantityChange so it triggers
        // fetchEstimatedEnd and updates family mold parameters (if any)
        this.onQuantityChange();
      }
    },
    async fetchEstimatedEnd() {
      if (this.plan.plannedquantity && this.plan.scheduledstart) {
        const runTime = Math.ceil(
          (+this.plan.plannedquantity / +this.plan.activecavity)
            * (+this.plan.stdcycletime * 1000),
        );
        this.plan.scheduledend = await this.getScheduledEnd({
          start: new Date(this.plan.scheduledstart).getTime(),
          duration: runTime,
        });
      } else {
        this.plan.scheduledend = '';
      }
    },
    onCavityChange() {
      if (this.familyParts && this.familyParts.length) {
        this.updateFamilyParams();
      }
    },
    onQuantityChange() {
      this.fetchEstimatedEnd();
      if (this.familyParts && this.familyParts.length) {
        this.updateFamilyParams();
      }
    },
    updateFamilyParams() {
      this.familyParts = this.familyParts.map((p) => ({
        ...p,
        plannedquantity: +p.activecavity * this.shots,
      }));
    },
    updateFamilyCavity({ id, payload }) {
      // eslint-disable-next-line
      const index = this.familyParts.findIndex((p) => p._id === id);
      this.$set(this.familyParts, index, {
        ...this.familyParts[index],
        plannedquantity: +payload * this.shots,
      });
    },
    async setSortIndex() {
      const lastPlan = await this.fetchLastPlan();
      if (lastPlan) {
        this.plan.sortindex = lastPlan.sortindex ? lastPlan.sortindex + 100 : 100;
      } else {
        this.plan.sortindex = 100;
      }
    },
    exit() {
      this.clear();
      this.selectedPart = null;
      this.$router.push({ name: 'productionPlanning' });
    },
    async save() {
      await this.setSortIndex();
      await this.fetchEstimatedEnd();
      const matrixTags = this.partMatrixTags(this.plan.assetid);
      const partMatrix = matrixTags.reduce((acc, cur) => {
        acc[cur.tagName] = this.selectedMatrix[cur.tagName];
        return acc;
      }, {});
      const selectedMachineDetails = this.machines.filter((item) => {
        const mc = item.machinename === this.selectedMachine;
        return mc;
      });
      let payload = [{
        ...partMatrix,
        ...this.plan,
        netweight: this.selectedPart
          && this.selectedPart.netweight !== undefined
          && this.selectedPart.netweight !== null
          && this.selectedPart.netweight !== ''
          ? parseFloat(this.selectedPart.netweight)
          : '',
        customer: this.selectedCustomer || this.plan.customer || '',
        color: this.selectedColor,
        scheduledstart: new Date(this.plan.scheduledstart).getTime(),
        scheduledend: this.plan.scheduledend
          ? new Date(this.plan.scheduledend).getTime()
          : '',
        familyName: 'family',
        eaid: selectedMachineDetails.length ? selectedMachineDetails[0].eaid : '',
      }];
      // eslint-disable-next-line
      const ids = [...new Set(this.selectedFamilyParts.map((p) => p._id))];
      // eslint-disable-next-line
      this.selectedFamilyParts = this.familyParts.filter((p) => ids.includes(p._id));
      if (this.selectedFamilyParts && this.selectedFamilyParts.length) {
        const familyPayload = this.selectedFamilyParts.map((p) => ({
          ...payload[0],
          cavity: p.cavity,
          activecavity: p.activecavity,
          plannedquantity: p.plannedquantity,
          partname: p.partname,
        }));
        payload = [...payload, ...familyPayload];
      }
      const created = await this.createPlans(payload);
      if (created) {
        this.setAlert({
          show: true,
          type: 'success',
          message: 'PLAN_CREATED',
        });
      } else {
        this.setAlert({
          show: true,
          type: 'error',
          message: 'ERROR_CREATING_PLAN',
        });
      }
      return created;
    },
    async onSaveAndAddNew() {
      this.savingAndNew = true;
      const created = await this.save();
      if (created) {
        this.clear();
        this.selectedPart = null;
      }
      this.savingAndNew = false;
    },
    async onSaveAndExit() {
      const isValid = await this.$refs.form.validate();
      if (isValid) {
        this.savingAndExit = true;
        const created = await this.save();
        if (created) {
          this.exit();
        }
        this.savingAndExit = false;
      }
    },
  },
};
</script>
